/* eslint-disable no-shadow */
import CheckIcon from "@mui/icons-material/Check";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import SyncIcon from "@mui/icons-material/Sync";
import { Box, Typography, useTheme } from "@mui/material";
import { createStyles, makeStyles } from "@mui/styles";
import { EditsquareIcon } from "@platformx/shared/static-assets";
import { BasicSwitch, ShowToastError, ShowToastSuccess } from "@platformx/utilities";
import { FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import JSONInput from "react-json-editor-ajrm";
import locale from "react-json-editor-ajrm/locale/en";
import "../PageSettings/PageSettings.css";
import { useCustomStyle } from "../PrelemSettings/PrelemSEO.style";
import { buildStructureDataFromSeoInfo, validateOverrideStructureData } from "../utils/helper";

const useStyles = makeStyles(() =>
  createStyles({
    rotateIcon: {
      animation: "$spin 5s linear infinite",
    },
    "@keyframes spin": {
      "0%": {
        transform: "rotate(360deg)",
      },
      "100%": {
        transform: "rotate(0deg)",
      },
    },
  }),
);

const PageSEO: FC<any> = ({
  structureDataEnvelope,
  setStructureData,
  seoInfoData,
  overrideMarkup = false,
  onOverrideMarkupChange,
  onValidationChange,
}) => {
  const classes = useCustomStyle();
  const Class = useStyles();
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState<boolean>(false);

  const { t } = useTranslation();
  // Viewer (toggle OFF) always shows the generated section; manual lives only in the textarea.
  const autoData = structureDataEnvelope?.auto ?? {};
  const [overrideText, setOverrideText] = useState<string>("");

  // Silent while typing; the code is reported up so the panel's Done stays gated too.
  const runValidation = (jsObject: any, hasParseError: boolean, parseErrorReason?: string) => {
    const result = validateOverrideStructureData(jsObject, hasParseError, parseErrorReason);
    onValidationChange?.(result.isValid, result.isValid ? undefined : result.code);
  };

  const validateOverrideText = () => {
    if (overrideText.trim() === "") {
      return validateOverrideStructureData(undefined, false);
    }
    try {
      return validateOverrideStructureData(JSON.parse(overrideText), false);
    } catch (error: any) {
      return validateOverrideStructureData(undefined, true, error?.message);
    }
  };

  const toggleStructureData = () => {
    setIsOpen(!isOpen);
  };

  // Invalid override markup toasts and keeps the dialog open.
  const handleDoneClick = () => {
    if (isOpen && overrideMarkup) {
      const result = validateOverrideText();
      if (!result.isValid) {
        const isMissingTags = result.code === "missing_context" || result.code === "missing_type";
        ShowToastError(
          t(
            isMissingTags ? "page_structure_data_missing_tags" : "page_structure_data_invalid_json",
          ),
        );
        return;
      }
    }
    toggleStructureData();
  };

  // Sync icon rebuilds only the auto section; a manual override is never touched by it.
  const reframeStructureData = () => {
    setLoading(true);
    const pageStructureData = buildStructureDataFromSeoInfo(seoInfoData);
    setStructureData((prev) => ({ ...prev, auto: pageStructureData }));
    setTimeout(() => {
      setLoading(false);
    }, 2000);
  };

  const handleOverrideTextChange = (event) => {
    const text = event.target.value;
    setOverrideText(text);
    if (text.trim() === "") {
      runValidation(undefined, false);
      return;
    }
    try {
      const parsed = JSON.parse(text);
      runValidation(parsed, false);
      setStructureData((prev) => ({ ...prev, manual: parsed }));
    } catch (error: any) {
      runValidation(undefined, true, error?.message);
    }
  };

  // Seed the editor from retained manual (falling back to current auto) and re-validate on ON.
  useEffect(() => {
    if (overrideMarkup) {
      const manualData = structureDataEnvelope?.manual ?? autoData;
      setOverrideText(JSON.stringify(manualData, undefined, 2));
      runValidation(manualData, false);
    } else {
      onValidationChange?.(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [overrideMarkup]);

  const copyStructureData = () => {
    const active = overrideMarkup ? (structureDataEnvelope?.manual ?? autoData) : autoData;
    navigator.clipboard.writeText(JSON.stringify(active, undefined, 2));
    ShowToastSuccess(`${t("page_structure_data")} ${t("copied")}`);
  };

  return (
    <Box
      className={`${classes.prelemSEOWrapper} pageSettingmainWp prelemSEOWrapperBg`}
      sx={{ padding: "0px !important" }}>
      <Box className='rowBox'>
        <Typography className='switchbox' variant='p4regular'>
          {t("page_structure_data")}
          <span
            style={{
              display: "flex",
              alignItems: "center",
              color: theme.palette.customColor.PRIMARY,
              textTransform: "capitalize",
            }}>
            <img
              src={EditsquareIcon}
              alt='icon'
              className='icon'
              style={{
                marginRight: "5px",
                filter:
                  Object.keys(autoData).length !== 0
                    ? `brightness(0) contrast(100%)`
                    : `filter: grayscale(100%)`,
                pointerEvents: Object.keys(autoData).length !== 0 ? "visible" : "none",
                cursor: "pointer",
                opacity: Object.keys(autoData).length !== 0 ? 1 : 0.3,
              }}
              onClick={toggleStructureData}
            />
            <SyncIcon
              sx={{
                color: "#2d2d39",
                verticalAlign: "middle",
                fontSize: { md: "21px" },
                cursor: "pointer",
              }}
              className={loading ? Class.rotateIcon : ""}
              onClick={reframeStructureData}
            />
          </span>
        </Typography>
      </Box>
      {isOpen && (
        <Box className='prelemSEOStructureDataOuter'>
          <Box className='prelemSEOStructureDataInner'>
            <Box
              className='buttonWrapper'
              sx={{ position: "relative", display: "flex", alignItems: "center", gap: "12px" }}>
              <Typography
                variant='p4regular'
                sx={{
                  display: "flex",
                  alignItems: "center",
                  margin: 0,
                  gap: "8px",
                  color: theme?.palette?.customColor?.PRIMARY || "#2d2d39",
                }}>
                {t("page_schema_markup_override")}
                <BasicSwitch
                  color={theme.palette.customColor.PRIMARY}
                  bgcolor={theme.palette.customColor.PLACEHOLDER}
                  onChange={onOverrideMarkupChange}
                  checked={overrideMarkup}
                  ariaLabel={t("page_schema_markup_override")}
                />
              </Typography>
              <ContentCopyIcon onClick={copyStructureData} sx={{ cursor: "pointer" }} />
              <CheckIcon
                data-testid='structure-data-done'
                onClick={handleDoneClick}
                sx={{ cursor: "pointer" }}
              />
            </Box>
            {overrideMarkup ? (
              <Box
                component='textarea'
                data-testid='structure-data-override-textarea'
                value={overrideText}
                onChange={handleOverrideTextChange}
                spellCheck={false}
                sx={{
                  width: "100%",
                  boxSizing: "border-box",
                  height: "95%",
                  minHeight: "240px",
                  padding: "12px",
                  borderRadius: "5px",
                  border: `1px solid ${theme.palette.customColor.LINE}`,
                  outline: "none",
                  backgroundColor: theme.palette.customColor.INPUT,
                  color: theme.palette.customColor.PRIMARY,
                  fontFamily: "'Roboto Mono', monospace",
                  fontSize: "16px",
                  lineHeight: 1.5,
                  resize: "vertical",
                  "&:focus-visible": {
                    outline: `2px solid ${theme.palette.customColor.PRIMARY}`,
                    outlineOffset: "-2px",
                  },
                }}
              />
            ) : (
              <JSONInput
                id='json-editor'
                confirmGood={false}
                placeholder={autoData}
                viewOnly
                theme='light_mitsuketa_tribute'
                locale={locale}
                height='95%'
                width='100%'
                colors={{
                  string: "#1984bc",
                  keys: theme.palette.customColor.PRIMARY,
                  colon: theme.palette.customColor.PRIMARY,
                  default: theme.palette.customColor.PRIMARY,
                }}
                style={{
                  body: { fontSize: "20px" },
                }}
              />
            )}
            {/* <ReactJson
              src={seoInfo.structureData}
              onEdit={handleStructureData} // Called when editing values
              onAdd={handleStructureData} // Called when adding new keys/values
              onDelete={handleStructureData} // Called when deleting keys/values
              theme='monokai'
              iconStyle='circle'
              displayDataTypes={false} // Optionally hide data types
              displayObjectSize={false} // Optionally hide object size
              collapseStringsAfterLength={30} // Optionally collapse long strings
              enableClipboard={false} // Disable copy-to-clipboard
              style={{ height: "50%", overflowY: "scroll" }}
            />
            <textarea
              placeholder='Paste your JSON here'
              onChange={handlePasteJson} // Call the function when losing focus
              style={{
                fontSize: "16px",
                padding: "10px",
                border: "1px solid #ccc",
                borderRadius: "5px",
                width: "100%",
                marginTop: "10px",
                height: "40%",
              }}
            /> */}
          </Box>
        </Box>
      )}
    </Box>
  );
};
export default PageSEO;
